
import { FormMetadata, IForm, IFormValidationError, IFormValidationResult, ResponseStatus } from '../../api/dtos';
import { useCallback, useContext, useMemo } from 'react';
import { IFormValidate } from '@ngt/forms-core';
import { asyncDebounce, OnlinePatientManagementContext, pascalToCameCasePropertyPath } from '@ngt/opms';


interface IFormValidationResponse {
    responseStatus?: ResponseStatus;
    validationResult?: IFormValidationResult;
}

export const useEConsentFormValidate = <T extends IForm = IForm>(formMetadata?: FormMetadata, consentGuid?: string, debounce?: number) => {
    const {serviceStackClient, dtos} = useContext(OnlinePatientManagementContext);

    const formDto = useMemo(() => {
        return dtos[`${formMetadata?.name}EConsentFormPostValidate`];
    }, [dtos, formMetadata])

    // @ts-ignore
    const handleValidate: IFormValidate<T, IFormValidationError> = useCallback(async (formState, _formActions) => {
        if (!formDto) {
            return {}
        };

        const { value } = formState;
        const response: IFormValidationResponse = await serviceStackClient.post(new formDto({ form: value!, guid: consentGuid }))
        const validationResult = response.validationResult;
        
        // parse errors into a format the form understands.
        const groupErrors = validationResult?.errors?.reduce<Record<string, IFormValidationError[]>>((a, b) => {
            const propertyName = pascalToCameCasePropertyPath(b.property!)
            if (!a[propertyName]) {
                a[propertyName] = [];
            }
            a[propertyName].push(b as IFormValidationError);
            return a;
        }, {}) ?? {};
        // return errors;
        return groupErrors;
    }, [serviceStackClient, formDto, consentGuid]);

    // debounce validation functions to reduce calls to the server and associate lag.
    const debouncedValidate = useMemo(() => {
        if (debounce === 0) {
            return handleValidate;
        }

        return asyncDebounce(handleValidate, debounce ?? 500);
    }, [handleValidate, debounce]);

    return debouncedValidate;
};

export default useEConsentFormValidate;